import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { useWaterAccounts } from "@/shared/selectors/WaterAccountSelector";
import { ErrorModal, useContextMenuMESF } from "@dexteel/mesf-core";
import { Paper } from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import Typography from "@mui/material/Typography";
import {
	DefaultMenuItem,
	GetContextMenuItemsParams,
	MenuItemDef,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useState } from "react";
import { useAllocationsOptionsFunctions } from "../hooks/useAllocationsOptionsFunctions";
import { useTableData } from "../hooks/useTableData";
import {
	buildAllocationRows,
	type WaterAccountAllocationRow,
} from "../models/WaterAccountAllocationDraft";
import { EditAllocationsModal } from "./EditAllocationsModal";

export const TableWaterAllocations = () => {
	const [showModal, setShowModal] = useState<"update" | "">("");
	const [selectedRowData, setSelectedRowData] =
		useState<WaterAccountAllocationRow | null>(null);
	const [error, setError] = useState("");

	const { data, isLoading, error: queryError } = useWaterAccounts();

	const accounts = data?.accounts ?? [];
	const allocations = data?.allocations ?? [];

	const targets = (data?.targets ?? []).filter((target) => target.IsActive);
	const rows = buildAllocationRows(
		accounts.filter((account) => account.IsSelectable && account.IsActive),
		allocations,
	);

	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const { getMenuOptions } = useAllocationsOptionsFunctions({
		setSelectedRowData,
		setShowModal,
	});
	const { columnDefs } = useTableData({
		targets,
		setSelectedRowData,
		setShowModal,
		showContextMenu,
	});

	const { defaultColDef } = useGridDefinitions({});

	const onRowDoubleClicked = (event: any) => {
		setSelectedRowData(event.data);
		setShowModal("update");
	};

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const data = params.node?.data;
			showContextMenu(event as any, data, "waterAllocationsContext");
			if (data) {
				params.api.deselectAll();
				params.node?.setSelected(true);
			}
			return [];
		},
		[rows],
	);

	useEffect(() => {
		registerConfig({
			id: "waterAllocationsContext",
			getOptions: getMenuOptions,
		});
	}, []);

	useEffect(() => {
		if (queryError) setError(queryError.message);
	}, [queryError]);

	return (
		<>
			<Grid2 container justifyContent="center" p={1} spacing={1}>
				<Grid2
					container
					size={{ md: 12, xs: 12 }}
					spacing={1}
					direction={"column"}
				>
					<Grid2>
						<Typography variant="h5" fontWeight={600}>
							Water Allocations
						</Typography>
						<Typography variant="body2" color="textSecondary">
							The default split of each account, used for every period no bill
							splits on its own.
						</Typography>
					</Grid2>
				</Grid2>
				<Grid2 container justifyContent="center" alignItems="center">
					<Grid2 size={{ md: 12, xs: 12 }}>
						<Paper elevation={2} style={{ height: "64vh", width: "100%" }}>
							<AgGridReact<WaterAccountAllocationRow>
								loading={isLoading}
								rowData={rows}
								columnDefs={columnDefs}
								defaultColDef={defaultColDef}
								rowHeight={34}
								headerHeight={30}
								animateRows
								getContextMenuItems={getContextMenuItems}
								onRowDoubleClicked={onRowDoubleClicked}
								loadingOverlayComponent={CenteredLazyLoading}
								getRowId={(params) => `${params.data.WaterAccountId}`}
								gridOptions={{
									rowSelection: {
										mode: "singleRow",
										checkboxes: false,
										enableClickSelection: true,
									},
								}}
							/>
						</Paper>
					</Grid2>
				</Grid2>
			</Grid2>
			<ErrorModal error={error} onHide={() => setError("")} />
			<EditAllocationsModal
				account={selectedRowData}
				allocations={allocations}
				targets={targets}
				modal={showModal}
				onHide={() => setShowModal("")}
			/>
		</>
	);
};
